import {
  ExamEventExamineeStatus,
  type GetExamEventExamineesForMonitoringQuery,
  ProgressStatus,
  useGetExamEventExamineesForMonitoringQuery,
} from '@exam-center/graphql';
import { useParams } from '@exam-center/router';
import { Flex, Text } from '@shared/components';
import { type TableBodyRow } from '@shared/table-generator';
import { useMemo } from 'react';

import { ExamineeMonitorStatusIcon, ExamineeMonitorTable } from '../../components';
import { headersWithStatusesForSession } from '../../components/examinee-monitor-table/columns';
import { useExamEventSessionExamineeSearchParams } from '../../hooks';
import { formatExamTime } from '../../utils';
import { ExamSessionExamineeMonitorMoreActionMenu } from '../exam-session-examinee-monitor-more-action-menu';
import { ExamSessionExamineeSearchBox } from '../exam-session-examinee-search-box';

const EXAMINEE_STATUS_MAP: Record<ExamEventExamineeStatus, string> = {
  [ExamEventExamineeStatus.Absent]: 'Absent',
  [ExamEventExamineeStatus.Canceled]: 'Canceled',
  [ExamEventExamineeStatus.Completed]: 'Completed',
  [ExamEventExamineeStatus.InProgress]: 'In Progress',
  [ExamEventExamineeStatus.Finished]: 'Finished',
  [ExamEventExamineeStatus.PendingResults]: 'Pending Results',
  [ExamEventExamineeStatus.PrepareForExam]: 'Prepare for Exam',
  [ExamEventExamineeStatus.Processing]: 'Processing',
  [ExamEventExamineeStatus.Registered]: 'Registered',
};

function generateRows(
  rows: GetExamEventExamineesForMonitoringQuery['ecGetExamEventExamineesForSession']['result'],
): TableBodyRow[] {
  return rows.map(({ examEventExaminee, examEndedAt }) => ({
    children: [
      {
        id: 'seatNumber',
        children: examEventExaminee.seatNumber?.toString() ?? '-',
      },
      {
        id: 'name',
        children: `${examEventExaminee.examinee.title} ${examEventExaminee.examinee.firstName} ${examEventExaminee.examinee.lastName}`,
      },
      { id: 'idNumber', children: examEventExaminee.examinee.idNumber },
      { id: 'course', children: examEventExaminee.examCourse.code },
      {
        id: 'status',
        children: EXAMINEE_STATUS_MAP[examEventExaminee.status],
      },
      {
        id: 'realtimeStatus.loginStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.loginStatus ?? ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'realtimeStatus.examStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.examStatus ?? ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'realtimeStatus.examSubmitStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.examSubmitStatus ??
              ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'realtimeStatus.questionnaireStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.questionnaireStatus ??
              ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'examEndedAt',
        children: <Text sx={{ textAlign: 'center' }}>{formatExamTime(examEndedAt)}</Text>,
      },
      {
        id: 'action',
        sx: (theme) => ({ padding: theme.spacing(1.5) }),
        children: <ExamSessionExamineeMonitorMoreActionMenu />,
      },
    ],
  }));
}

export default function ExamSessionExamineeMonitorList() {
  const { examSessionId, examEventId } = useParams(
    '/:examCenterCode/proctoring/exam-events/:examEventId/details/session/:examSessionId',
  );
  const [{ search }] = useExamEventSessionExamineeSearchParams();

  const { data, loading: isExamEventSessionExamineesLoading } =
    useGetExamEventExamineesForMonitoringQuery({
      variables: {
        sessionId: examSessionId,
        examEventId,
        searchExaminee: {
          filter: {
            searchText: {
              fields: ['fullName'],
              value: search || '',
            },
          },
        },
        paging: null,
      },
      fetchPolicy: 'cache-and-network',
      pollInterval: 10_000,
    });

  const examEventSessionExaminees = useMemo(
    () => data?.ecGetExamEventExamineesForSession?.result || [],
    [data?.ecGetExamEventExamineesForSession?.result],
  );

  const isInitialLoading = isExamEventSessionExamineesLoading && !data;

  const rows = useMemo(() => generateRows(examEventSessionExaminees), [examEventSessionExaminees]);

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(6),
      })}
    >
      <ExamSessionExamineeSearchBox />
      <ExamineeMonitorTable
        rows={rows}
        columns={headersWithStatusesForSession}
        isLoading={isInitialLoading}
        slotProps={{
          tableHeaderCell: {
            sx: (theme) => ({
              p: theme.spacing(3),
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              borderRight: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.grey[100],
              ...theme.typography.body1Semibold,
            }),
          },
          tableBodyRow: {
            sx: {
              '&:last-child td': {
                borderBottom: 'none',
              },
            },
          },
          tableBodyCell: {
            sx: (theme) => ({
              p: theme.spacing(2, 3),
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              borderRight: `1px solid ${theme.palette.grey[300]}`,
              ...theme.typography.body1,
              '&:last-child': {
                borderRight: 'none',
              },
              '&:nth-of-type(1), &:nth-of-type(n+6):nth-of-type(-n+10)': {
                textAlign: 'center',
              },
            }),
          },
        }}
      />
    </Flex>
  );
}
